import { type TimeSlot } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Link, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { MyShiftCard } from "@src/appV2/redesign/Shift/Card/MyShiftCard";
import {
  BottomSheet,
  DialogFooter,
  Illustration,
  LoadingSpinner,
  SystemInlineBanner,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import Lottie from "lottie-react";
import pluralize from "pluralize";

import { LoadingButton } from "../../components/LoadingButton";
import { LoadingTextButton } from "../../components/LoadingTextButton";
import clipboardMarkSuccessAnimation from "../../Onboarding/components/animations/clipboardMarkSuccess.json";
import { ReferralPostBookingCta } from "./ReferralPostBookingCta/ReferralPostBookingCta";

export interface RegularShiftBookedBottomSheetProps {
  modalState: UseModalState;
  onWorkplaceActionClick: () => void;
  workplaceActionLabel: string;
  isWorkplaceActionLoading: boolean;
  isLoadingPendingPostBookingRequirements: boolean;
  shiftId: string;
  workplaceId: string;
  workplaceType: string | undefined;
  msa: string | undefined;
  shiftStartIso: string;
  shiftEndIso: string;
  shiftTimeSlot: TimeSlot;
  workplaceName: string;
  workplaceTimezone: string | undefined;
  shiftHourlyPay: string | undefined;
  shiftFormattedTotalPay: string | undefined;
  shiftTotalPay: number | undefined;
  shiftBoost: number | undefined;
  requiresBreak: boolean | undefined;
  breakDurationInMinutes: number;
  isLoadingQualification: boolean;
  qualificationText: string;
  pendingPostBookingRequirementsCount?: number;
  onCompleteDocumentsClick?: () => void;
}

export function RegularShiftBookedBottomSheet(props: RegularShiftBookedBottomSheetProps) {
  const {
    modalState,
    onWorkplaceActionClick,
    workplaceActionLabel,
    isWorkplaceActionLoading,
    shiftStartIso,
    shiftEndIso,
    shiftTimeSlot,
    workplaceName,
    workplaceTimezone,
    shiftHourlyPay,
    shiftFormattedTotalPay,
    shiftTotalPay,
    shiftBoost,
    requiresBreak,
    breakDurationInMinutes,
    isLoadingQualification,
    qualificationText,
    isLoadingPendingPostBookingRequirements,
    pendingPostBookingRequirementsCount = 0,
    onCompleteDocumentsClick,
    shiftId,
    workplaceId,
    workplaceType,
    msa,
  } = props;

  const hasPendingPostBookingRequirements =
    pendingPostBookingRequirementsCount > 0 && isDefined(onCompleteDocumentsClick);

  const isMainCTALoading = isWorkplaceActionLoading || isLoadingPendingPostBookingRequirements;

  return (
    <BottomSheet
      modalState={modalState}
      header={
        <BottomSheetHeader
          onClose={() => {
            modalState.closeModal();
          }}
        />
      }
      footer={
        <DialogFooter orientation="vertical" LinkComponent={Link}>
          <ReferralPostBookingCta
            shiftId={shiftId}
            workplaceId={workplaceId}
            workplaceType={workplaceType}
            msa={msa}
          />
          {hasPendingPostBookingRequirements ? (
            <>
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                isLoading={isMainCTALoading}
                onClick={onCompleteDocumentsClick}
              >
                <Stack direction="row" alignItems="center" spacing={4}>
                  <Text inline color="inherit">
                    Complete {pluralize("Document", pendingPostBookingRequirementsCount)}
                  </Text>
                </Stack>
              </LoadingButton>
              <LoadingTextButton
                size="medium"
                isLoading={isWorkplaceActionLoading}
                onClick={() => {
                  onWorkplaceActionClick();
                }}
              >
                {workplaceActionLabel}
              </LoadingTextButton>
            </>
          ) : (
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              isLoading={isMainCTALoading}
              onClick={() => {
                onWorkplaceActionClick();
              }}
            >
              {workplaceActionLabel}
            </LoadingButton>
          )}
        </DialogFooter>
      }
    >
      <DialogContent sx={{ backgroundColor: "unset" }}>
        <Stack direction="column" alignItems="center" spacing={6}>
          <Stack direction="column" alignItems="center" spacing={0}>
            {isRebrandingEnabled() ? (
              <Box sx={{ width: 240, marginBottom: "-40px" }}>
                <Lottie loop animationData={clipboardMarkSuccessAnimation} />
              </Box>
            ) : (
              <Illustration type="shift-booked" />
            )}
            <Title variant="h2" component="h2" sx={{ textAlign: "center", width: "14rem" }}>
              Your shift is booked!
            </Title>
          </Stack>

          {isLoadingPendingPostBookingRequirements && (
            <LoadingSpinner size="medium" variant="secondary" />
          )}
          {!isLoadingPendingPostBookingRequirements && hasPendingPostBookingRequirements && (
            <SystemInlineBanner
              color="warning"
              iconType="triangle-exclamation"
              sx={{ width: "100%" }}
            >
              <Text inline variant="body2" color="inherit">
                The workplace requires a bit of paperwork before your shift.{" "}
              </Text>
              <Text semibold inline variant="body2" color="inherit">
                {pendingPostBookingRequirementsCount}{" "}
                {pluralize("document", pendingPostBookingRequirementsCount)} to review and
                sign.{" "}
              </Text>
              <Text inline variant="body2" color="inherit">
                Please complete {pendingPostBookingRequirementsCount === 1 ? "it" : "them"} before
                your shift.
              </Text>
            </SystemInlineBanner>
          )}

          <MyShiftCard
            outlined
            shiftStart={shiftStartIso}
            shiftEnd={shiftEndIso}
            shiftTimeSlot={shiftTimeSlot}
            workplaceName={workplaceName}
            workplaceTimezone={workplaceTimezone}
            shiftHourlyPay={shiftHourlyPay}
            shiftFormattedTotalPay={shiftFormattedTotalPay}
            shiftTotalPay={shiftTotalPay}
            shiftBoost={shiftBoost}
            requiresBreak={requiresBreak}
            breakDurationInMinutes={breakDurationInMinutes}
            isLoadingQualification={isLoadingQualification}
            qualificationText={qualificationText}
          />
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
